/**
 * FarmlyAvatar – Single source of truth for all avatar rendering in Farmly.
 *
 * Neutral linen palette, never orange, never theme-dependent.
 * Used in topbars, menus, profile pages, and team lists.
 */
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

const sizes = {
  xs: "h-7 w-7 text-[10px]",
  sm: "h-9 w-9 text-xs",
  md: "h-12 w-12 text-sm",
  lg: "h-16 w-16 text-lg",
  xl: "h-20 w-20 text-xl",
} as const;

export type FarmlyAvatarSize = keyof typeof sizes;

interface FarmlyAvatarProps {
  /** URL for profile image – falls back to initials when absent or broken */
  imageUrl?: string | null;
  /** Initials to show in fallback (max 2 chars) */
  initials: string;
  /** Alt text for the image */
  alt?: string;
  /** Predefined size token */
  size?: FarmlyAvatarSize;
  /** Extra className on outer Avatar wrapper */
  className?: string;
  /** data-testid forwarded to fallback */
  "data-testid"?: string;
}

export function FarmlyAvatar({
  imageUrl,
  initials,
  alt,
  size = "sm",
  className,
  "data-testid": testId,
}: FarmlyAvatarProps) {
  const sizeClass = sizes[size];
  // Extract only the dimension classes for the outer Avatar
  const dimensionClass = sizeClass.replace(/text-\S+/g, "").trim();
  // Extract only the text-size class for the fallback
  const textClass = sizeClass.match(/text-\S+/)?.[0] ?? "";

  return (
    <Avatar className={cn(dimensionClass, "border border-[#D8D2CA]", className)}>
      {imageUrl && <AvatarImage src={imageUrl} alt={alt} />}
      <AvatarFallback
        data-testid={testId}
        className={cn(textClass, "font-medium bg-[#E8E4DE] text-[#3A342E]")}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
